import { useState, useCallback, useEffect } from 'react'
import {
  ChevronDown, ChevronUp, Zap, Loader2, X, AlertTriangle,
} from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible, ResultBadge,
  PayloadEditor, CurlCollapsible, EndpointPicker,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { proxyUrl, buildHeaders } from './utils'
import type { Creds, LogEntry } from './types'

const KYC_V1_ENDPOINTS: EndpointDef[] = [
  { id: 'create-verify', method: 'POST', label: 'Create + Verify', path: '/entity/new/verify/profile/simple', category: 'Entity Management', core: true },
  { id: 'get-checks', method: 'GET', label: 'Entity Checks', path: '/entity/{entityId}/checks', category: 'Results', core: true },
  { id: 'get-entity', method: 'GET', label: 'Entity Details', path: '/entity/{entityId}', category: 'Entity Management', core: true },
  // Entity Management
  { id: 'create-entity', method: 'POST', label: 'Create Entity (no verify)', path: '/entity/new', category: 'Entity Management' },
  { id: 'update-verify', method: 'POST', label: 'Update + Re-verify', path: '/entity/{entityId}/verify/profile/{resultLevel}', category: 'Entity Management' },
  { id: 'update-entity', method: 'POST', label: 'Update Entity', path: '/entity/{entityId}', category: 'Entity Management' },
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Entity', path: '/entity/{entityId}', category: 'Entity Management' },
  // Results
  { id: 'retrieve-response', method: 'GET', label: 'Retrieve Response', path: '/retrieve/response/{requestId}', description: '3-day cache', category: 'Results' },
  // Documents
  { id: 'verify-document', method: 'POST', label: 'Create + Verify Document', path: '/entity/{entityId}/document/verify', category: 'Documents' },
  { id: 'get-doc-checks', method: 'GET', label: 'Document Check Details', path: '/entity/{entityId}/document/{documentId}/checks', category: 'Documents' },
]

// ── shared helpers ─────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

// ── V1 types ───────────────────────────────────────────────────────────────────

interface V1KycForm {
  givenName: string; middleName: string; familyName: string
  dob: string; gender: string
  streetNumber: string; streetName: string; town: string; state: string
  postalCode: string; country: string
  docType: string; docNumber: string; docExpiry: string; docCountry: string
  docRegion: string; docCardNumber: string; docSubType: string; docReference: string
  includeDoc: boolean; entityProfile: string
}

const BLANK_V1_FORM: V1KycForm = {
  givenName: '', middleName: '', familyName: '', dob: '', gender: 'M',
  streetNumber: '1', streetName: 'Test Street', town: 'SYDNEY', state: 'NSW',
  postalCode: '2000', country: 'AUS',
  docType: 'PASSPORT', docNumber: '', docExpiry: '', docCountry: 'AUS',
  docRegion: '', docCardNumber: '', docSubType: 'G', docReference: '1',
  includeDoc: false, entityProfile: 'safe_harbour',
}

interface V1Scenario { label: string; expectedResult: 'PASS' | 'FAIL' | 'REVIEW'; form: Partial<V1KycForm> }

const KYC_V1_SCENARIOS: V1Scenario[] = [
  { label: "PASS — JAMES A TESTONE (Driver's License)", expectedResult: 'PASS', form: { givenName: 'JAMES', middleName: 'A', familyName: 'TESTONE', dob: '1950-01-01', gender: 'M', streetNumber: '35', streetName: 'CONN STREET', town: 'FERNTREE GULLY', state: 'VIC', postalCode: '3156', country: 'AUS', docType: 'DRIVERS_LICENCE', docNumber: '283229690', docExpiry: '', docCountry: 'AUS', docRegion: 'VIC', docCardNumber: 'P5403241', includeDoc: true } },
  { label: "FAIL — JENNY TESTEIGHT (Driver's License)", expectedResult: 'FAIL', form: { givenName: 'JENNY', middleName: '', familyName: 'TESTEIGHT', dob: '1957-01-01', gender: 'F', streetNumber: '56', streetName: 'VICTORIA STREET', town: 'FINGAL', state: 'TAS', postalCode: '7214', country: 'AUS', docType: 'DRIVERS_LICENCE', docNumber: 'T56543', docExpiry: '', docCountry: 'AUS', docRegion: 'TAS', docCardNumber: 'T03989853', includeDoc: true } },
  { label: 'PASS — PETER TESTTHIRTEEN (Passport)', expectedResult: 'PASS', form: { givenName: 'PETER', middleName: '', familyName: 'TESTTHIRTEEN', dob: '1962-01-01', gender: 'M', streetNumber: '19', streetName: 'LIBANANGU ROAD', town: 'KALKARINDJI', state: 'NT', postalCode: '0852', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'P77864526', docExpiry: '', docCountry: 'AUS' } },
  { label: 'REVIEW — PEP — JAMES G TESTELEVEN (Passport)', expectedResult: 'REVIEW', form: { givenName: 'JAMES', middleName: 'G', familyName: 'TESTELEVEN', dob: '1960-01-01', gender: 'M', streetNumber: '23', streetName: 'ISA STREET', town: 'FYSHWICK', state: 'ACT', postalCode: '2609', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'E55173628', docExpiry: '', docCountry: 'AUS' } },
  { label: 'REVIEW — Sanction — BRUCE TESTNINETEEN (Passport)', expectedResult: 'REVIEW', form: { givenName: 'BRUCE', middleName: '', familyName: 'TESTNINETEEN', dob: '1968-01-01', gender: 'M', streetNumber: '12', streetName: 'THROSSELL STREET', town: 'CURTIN', state: 'ACT', postalCode: '2605', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'P32516383', docExpiry: '', docCountry: 'AUS' } },
  { label: 'PASS — MARY TESTFOUR (Medicare)', expectedResult: 'PASS', form: { givenName: 'MARY', middleName: '', familyName: 'TESTFOUR', dob: '1953-01-01', gender: 'F', streetNumber: '17', streetName: 'WATTLE AVENUE', town: 'WARWICK', state: 'QLD', postalCode: '4370', country: 'AUS', includeDoc: true, docType: 'NATIONAL_HEALTH_ID', docNumber: '2980366171', docExpiry: '2030-01-01', docCountry: 'AUS', docSubType: 'B', docReference: '2' } },
]

const V1_BASE = 'https://api.kycaml.uat.frankiefinancial.io/compliance/v1.2'

const ENTITY_PROFILES = ['safe_harbour', 'customer', 'aml_gateway', 'custom', 'default']
const GENDERS = ['M', 'F', 'U']
const DOC_TYPES = ['PASSPORT', 'DRIVERS_LICENCE', 'NATIONAL_HEALTH_ID', 'NATIONAL_ID', 'BIRTH_CERT', 'TRAVEL_DOC', 'IMMIGRATION_CARD', 'CITIZENSHIP_CERT']
const AU_STATES = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

function ComboInput({ value, onChange, options, className }: { value: string; onChange: (v: string) => void; options: string[]; className?: string }) {
  const [open, setOpen] = useState(false)
  const filtered = options.filter(o => o.toLowerCase().includes(value.toLowerCase()))
  return (
    <div className="relative">
      <input
        className={className ?? inputCls}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-20 left-0 right-0 mt-0.5 rounded-[7px] overflow-hidden shadow-lg"
          style={{ background: '#0e1828', border: '1px solid #233149', maxHeight: 160, overflowY: 'auto' }}
        >
          {filtered.map(o => (
            <button
              key={o}
              onMouseDown={e => { e.preventDefault(); onChange(o); setOpen(false) }}
              className="w-full text-left px-2.5 py-1.5 text-[11px] font-mono transition-colors hover:bg-[#16223a]"
              style={{ color: o === value ? '#c7d2fe' : '#94a3b8' }}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function buildV1Payload(form: V1KycForm) {
  const entity: AnyObj = {
    entityProfile: form.entityProfile,
    entityType: 'INDIVIDUAL',
    name: { givenName: form.givenName, ...(form.middleName.trim() ? { middleName: form.middleName.trim() } : {}), familyName: form.familyName },
    dateOfBirth: { dateOfBirth: form.dob },
    gender: form.gender,
    addresses: [{ addressType: 'RESIDENTIAL1', streetNumber: form.streetNumber, streetName: form.streetName, town: form.town, state: form.state, postalCode: form.postalCode, country: form.country }],
    extraData: [
      { kvpKey: 'consent.general', kvpValue: 'true', kvpType: 'general.bool' },
      { kvpKey: 'consent.docs', kvpValue: 'true', kvpType: 'general.bool' },
      { kvpKey: 'consent.creditheader', kvpValue: 'true', kvpType: 'general.bool' },
    ],
  }
  if (form.includeDoc && form.docNumber) {
    const doc: AnyObj = { idType: form.docType, idNumber: form.docNumber, country: form.docCountry }
    if (form.docExpiry) doc.idExpiry = form.docExpiry
    if (form.docType === 'DRIVERS_LICENCE') {
      if (form.docRegion) doc.region = form.docRegion
      if (form.docCardNumber) doc.extraData = [{ kvpKey: 'document_number', kvpType: 'general.string', kvpValue: form.docCardNumber }]
    }
    if (form.docType === 'NATIONAL_HEALTH_ID') {
      if (form.docSubType) doc.idSubType = form.docSubType
      doc.extraData = [{ kvpKey: 'reference', kvpValue: form.docReference || '1' }]
    }
    entity.identityDocs = [doc]
  }
  return { entity }
}

// ── Result display ─────────────────────────────────────────────────────────────

function V1ResultPanel({ data, fullResponse }: { data: AnyObj; fullResponse: unknown }) {
  const epr = data?.entityProfileResult
  const entityId = data?.entity?.entityId ?? data?.entityId
  const cr = data?.checkRisk
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function kvpMap(notes: any[]): Record<string, string> {
    if (!Array.isArray(notes)) return {}
    const m: Record<string, string> = {}
    for (const n of notes) m[n.kvpKey] = n.kvpValue
    return m
  }
  const crNotes = kvpMap(cr?.resultNotes ?? [])
  const riskScore = cr?.riskLevel as number | undefined
  const riskLevel = crNotes['Risk.Level']
  const riskBracket = crNotes['Risk.Bracket']
  const confidenceLevel = crNotes['Confidence.Level']

  const riskFactors: { name: string; value: string; score: string }[] = []
  for (const key of Object.keys(crNotes)) {
    const m = key.match(/^risk_factor\.(.+)\.value$/)
    if (m) {
      const factor = m[1]
      riskFactors.push({ name: factor, value: crNotes[key], score: crNotes[`risk_factor.${factor}.score`] ?? '' })
    }
  }

  const cs = data?.checkSummary
  const csNotes = kvpMap(cs?.resultNotes ?? [])
  const matchCounts = [
    { label: 'Name', key: 'Info.NameMatchCount' },
    { label: 'Address', key: 'Info.AddressMatchCount' },
    { label: 'DoB', key: 'Info.DoBMatchCount' },
    { label: 'Gov ID', key: 'Info.GovIDMatchCount' },
    { label: 'Other ID', key: 'Info.OtherIDMatchCount' },
  ].filter(m => csNotes[m.key] != null)

  const issueList: string[] = epr?.issueList ?? []

  const BADGE_RISK: Record<string, string> = {
    UNACCEPTABLE: 'rgba(248,113,113,.15)',
    HIGH: 'rgba(248,113,113,.15)',
    MEDIUM: 'rgba(251,146,60,.15)',
    LOW: 'rgba(52,211,153,.15)',
  }
  const BADGE_RISK_COLOR: Record<string, string> = {
    UNACCEPTABLE: '#f87171', HIGH: '#f87171', MEDIUM: '#fb923c', LOW: '#34d399',
  }

  function factorLabel(raw: string) {
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }
  function factorValueLabel(factor: string, val: string) {
    if (factor === 'confidence') return val
    return val === '1' || val === 'true' ? 'Yes' : val === '0' || val === 'false' ? 'No' : val
  }

  return (
    <div className="space-y-3">
      {epr && (
        <div className="rounded-[11px] p-4" style={{ background: '#0e1828', border: '1px solid #1e2c44' }}>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            {epr.actionRecommended && <ResultBadge result={epr.actionRecommended} />}
            {riskLevel && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: BADGE_RISK[riskLevel] ?? 'rgba(148,163,184,.15)', color: BADGE_RISK_COLOR[riskLevel] ?? '#94a3b8' }}>
                {riskLevel} RISK
              </span>
            )}
            {issueList.map(issue => (
              <span key={issue} className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(248,113,113,.15)', color: '#f87171' }}>{issue}</span>
            ))}
          </div>
          {riskScore != null && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="text-[#5b6b85] font-mono">Risk Score</span>
                <span className="font-bold font-mono" style={{ color: riskScore >= 80 ? '#f87171' : riskScore >= 50 ? '#fb923c' : '#34d399' }}>{riskScore} / 100</span>
              </div>
              <div className="h-[4px] rounded-full" style={{ background: '#16223a' }}>
                <div className="h-full rounded-full" style={{ width: `${riskScore}%`, background: riskScore >= 80 ? '#f87171' : riskScore >= 50 ? '#fb923c' : '#34d399' }} />
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 flex-wrap text-[10px] font-mono text-[#5b6b85]">
            {riskBracket && <span>Bracket: <span className="text-[#94a3b8]">{riskBracket}</span></span>}
            {confidenceLevel && <span>Confidence: <span className="text-[#94a3b8]">{confidenceLevel}</span></span>}
            {epr.profileName && <span>Profile: <span className="text-[#94a3b8]">{epr.profileName}</span></span>}
            {entityId && <span>entityId: <span className="text-[#94a3b8] text-[9px]">{entityId}</span></span>}
          </div>
        </div>
      )}

      {epr?.checkResults?.length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Check Results</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#1a2740' }}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {epr.checkResults.map((check: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[11px] text-[#94a3b8] flex-1 font-mono">{check.name ?? check.checkType ?? '—'}</span>
                <ResultBadge result={check.result ?? '—'} />
                {check.message && <span className="text-[9px] text-[#5b6b85] italic truncate">{check.message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {riskFactors.length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Risk Factors</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#1a2740' }}>
            {riskFactors.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[11px] text-[#94a3b8] flex-1 font-mono">{factorLabel(f.name)}</span>
                <span className="text-[10px] font-mono text-[#5b6b85]">{factorValueLabel(f.name, f.value)}</span>
                <span className="text-[10px] font-mono text-[#94a3b8] w-8 text-right">{f.score !== '' ? f.score : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {matchCounts.length > 0 && (
        <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-3">Match Counts</p>
          <div className="flex items-center gap-4 flex-wrap">
            {matchCounts.map(m => {
              const count = parseInt(csNotes[m.key] ?? '0')
              return (
                <div key={m.label} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-[#5b6b85]">{m.label}:</span>
                  <span className="font-bold font-mono" style={{ color: count > 0 ? '#34d399' : '#475569' }}>{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <RawJsonCollapsible data={fullResponse} />
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface KycV1WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function KycV1Workspace({ creds, addLog }: KycV1WorkspaceProps) {
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const v1Creds = { ...creds, apiBase: V1_BASE }

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('create-verify')
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [form, setForm] = useState<V1KycForm>(BLANK_V1_FORM)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [loading, setLoading] = useState<string | null>(null)
  const [result, setResult] = useState<AnyObj | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const [formOpen, setFormOpen] = useState(true)

  // PayloadEditor state
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  const payload = buildV1Payload(form)

  // Sync derived payload → editor (unless user edited)
  const derivedJson = JSON.stringify(payload)
  useEffect(() => {
    if (!editorDirty) {
      setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      setEditorError(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedJson, editorDirty])

  // Auto-populate GET inputs from prior result
  useEffect(() => { if (entityId) setGetEntityIdInput(entityId) }, [entityId])

  function handleEditorChange(v: string) { setEditorText(v); setEditorDirty(true); setEditorError(null) }
  function resetEditor() { setEditorDirty(false); setEditorText(JSON.stringify(payload, null, 2)); setEditorError(null) }
  function getEditorPayload(): Record<string, unknown> | null {
    try { return JSON.parse(editorText) as Record<string, unknown> }
    catch { setEditorError('Invalid JSON — fix syntax before sending'); return null }
  }

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${V1_BASE}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(v1Creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [v1Creds, addLog])

  async function createEntity() {
    const ep = getEditorPayload()
    if (!ep) return
    setLoading('create'); setResult(null); setFullResult(null)
    try {
      const data = await doFetch('POST', '/entity/new/verify/profile/simple', ep, 'V1 KYC: Create + verify individual (synchronous)') as AnyObj
      setResult(data); setFullResult(data)
      const eid = data?.entity?.entityId as string | undefined
      if (eid) setEntityId(eid)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getChecks() {
    const eid = getEntityIdInput || entityId
    if (!eid) return
    setLoading('checks')
    try {
      const data = await doFetch('GET', `/entity/${eid}/checks`, undefined, 'V1 KYC: GET /entity/{entityId}/checks') as AnyObj
      setResult(data); setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getEntity() {
    const eid = getEntityIdInput || entityId
    if (!eid) return
    setLoading('get')
    try {
      const data = await doFetch('GET', `/entity/${eid}`, undefined, 'V1 KYC: Re-fetch entity result') as AnyObj
      setResult(data); setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  const isRunning = loading === 'create'
  const epDef = KYC_V1_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? KYC_V1_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'
  const dynamicPath = selectedEndpoint === 'create-verify' ? '/entity/new/verify/profile/simple'
    : selectedEndpoint === 'get-checks' ? `/entity/${getEntityIdInput || '{entityId}'}/checks`
    : `/entity/${getEntityIdInput || '{entityId}'}`

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <div className="space-y-3">
        <p className="text-[9px] font-bold uppercase tracking-wider text-[#3f4f6b] px-1">Endpoints</p>
        <EndpointPicker endpoints={KYC_V1_ENDPOINTS} selectedId={selectedEndpoint} onSelect={handleEndpointSelect} />
      </div>

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono shrink-0"
            style={isGetEndpoint ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' } : { background: 'rgba(52,211,153,.13)', color: '#34d399' }}>
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">{dynamicPath}</span>
          <span className="text-[9px] font-mono text-[#3f4f6b]">V1</span>
        </div>

        <div className="p-4 space-y-4">
          {/* create-verify: full form */}
          {selectedEndpoint === 'create-verify' && (
            <>
              <div>
                <SectionLabel>Entity Profile (Recipe)</SectionLabel>
                <ComboInput value={form.entityProfile} onChange={v => setForm(f => ({ ...f, entityProfile: v }))} options={ENTITY_PROFILES} />
                <p className="text-[9px] font-mono text-[#3f4f6b] mt-1">entityProfile: <span className="text-amber-400">{form.entityProfile}</span></p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-[#3f4f6b] mb-2">Test Persona · Auto-fill</p>
                <div className="flex flex-wrap gap-1.5">
                  {KYC_V1_SCENARIOS.map((s, i) => {
                    const isActive = scenarioIdx === i
                    const dotColor = s.expectedResult === 'PASS' ? '#34d399' : s.expectedResult === 'FAIL' ? '#f87171' : '#fb923c'
                    return (
                      <button
                        key={i}
                        onClick={() => { setScenarioIdx(i); setForm({ ...BLANK_V1_FORM, ...s.form, entityProfile: form.entityProfile }); setResult(null); setFullResult(null); setEntityId(null) }}
                        className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] font-medium transition-all"
                        style={isActive ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#e2e8f0' } : { background: '#0f1a2e', border: '1px solid #233149', color: '#94a3b8' }}
                      >
                        <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background: dotColor }} />
                        <span className="truncate max-w-[120px]">{s.label.split('—')[1]?.trim() ?? s.label}</span>
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: s.expectedResult === 'PASS' ? 'rgba(52,211,153,.15)' : s.expectedResult === 'FAIL' ? 'rgba(248,113,113,.15)' : 'rgba(251,146,60,.15)', color: s.expectedResult === 'PASS' ? '#34d399' : s.expectedResult === 'FAIL' ? '#f87171' : '#fb923c' }}>{s.expectedResult}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <button onClick={() => setFormOpen(o => !o)} className="w-full flex items-center justify-between py-1.5">
                  <SectionLabel>Individual data</SectionLabel>
                  {formOpen ? <ChevronUp size={13} className="text-[#5b6b85]" /> : <ChevronDown size={13} className="text-[#5b6b85]" />}
                </button>
                {formOpen && (
                  <div className="space-y-3 pt-1">
                    <SectionLabel>Personal Details</SectionLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {([['Given Name', 'givenName'], ['Middle Name', 'middleName'], ['Family Name', 'familyName']] as const).map(([label, key]) => (
                        <div key={key}><label className={labelCls}>{label}</label><input className={inputCls} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} /></div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className={labelCls}>Date of Birth</label><input className={inputCls} placeholder="YYYY-MM-DD" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} /></div>
                      <div><label className={labelCls}>Gender</label><ComboInput value={form.gender} onChange={v => setForm(f => ({ ...f, gender: v }))} options={GENDERS} /></div>
                    </div>
                    <SectionLabel>Address</SectionLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className={labelCls}>Street Number</label><input className={inputCls} value={form.streetNumber} onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))} /></div>
                      <div><label className={labelCls}>Street Name</label><input className={inputCls} value={form.streetName} onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))} /></div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {([['Town', 'town'], ['State', 'state'], ['Postcode', 'postalCode'], ['Country', 'country']] as const).map(([label, key]) => (
                        <div key={key}><label className={labelCls}>{label}</label><input className={inputCls} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} /></div>
                      ))}
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-[11px] text-[#94a3b8] cursor-pointer mb-2">
                        <input type="checkbox" checked={form.includeDoc} onChange={e => setForm(f => ({ ...f, includeDoc: e.target.checked }))} style={{ accentColor: '#6366f1' }} />
                        Include identity document
                      </label>
                      {form.includeDoc && (
                        <div className="space-y-2 pl-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div><label className={labelCls}>Doc Type</label><ComboInput value={form.docType} onChange={v => setForm(f => ({ ...f, docType: v }))} options={DOC_TYPES} /></div>
                            <div><label className={labelCls}>{form.docType === 'NATIONAL_HEALTH_ID' ? 'Card Number' : 'Doc Number'}</label><input className={inputCls} value={form.docNumber} onChange={e => setForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          </div>
                          {form.docType !== 'NATIONAL_HEALTH_ID' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={labelCls}>Expiry (YYYY-MM-DD)</label><input className={inputCls} value={form.docExpiry} onChange={e => setForm(f => ({ ...f, docExpiry: e.target.value }))} /></div>
                              <div><label className={labelCls}>Country</label><input className={inputCls} value={form.docCountry} onChange={e => setForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                            </div>
                          )}
                          {form.docType === 'DRIVERS_LICENCE' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={labelCls}>State (region)</label><ComboInput value={form.docRegion} onChange={v => setForm(f => ({ ...f, docRegion: v }))} options={AU_STATES} /></div>
                              <div><label className={labelCls}>Card Number</label><input className={inputCls} value={form.docCardNumber} onChange={e => setForm(f => ({ ...f, docCardNumber: e.target.value }))} /></div>
                            </div>
                          )}
                          {form.docType === 'NATIONAL_HEALTH_ID' && (
                            <div className="grid grid-cols-3 gap-2">
                              <div><label className={labelCls}>Card Colour</label><ComboInput value={form.docSubType} onChange={v => setForm(f => ({ ...f, docSubType: v }))} options={['G', 'B', 'Y']} /></div>
                              <div><label className={labelCls}>Expiry</label><input className={inputCls} placeholder="YYYY-MM-DD" value={form.docExpiry} onChange={e => setForm(f => ({ ...f, docExpiry: e.target.value }))} /></div>
                              <div><label className={labelCls}>Reference (1–5)</label><input className={inputCls} value={form.docReference} onChange={e => setForm(f => ({ ...f, docReference: e.target.value }))} /></div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* get-checks / get-entity: entityId input */}
          {(selectedEndpoint === 'get-checks' || selectedEndpoint === 'get-entity') && (
            <div>
              <label className={labelCls}>Entity ID <span style={{ color: '#f87171' }}>*</span></label>
              <input className={inputCls} value={getEntityIdInput} onChange={e => setGetEntityIdInput(e.target.value)} placeholder="auto-filled from prior result" />
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-1">
            {selectedEndpoint === 'create-verify' && (
              <button
                onClick={() => void createEntity()}
                disabled={loading !== null || !hasCreds}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'create' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Creating…</span> : 'Create + Verify'}
              </button>
            )}
            {selectedEndpoint === 'get-checks' && (
              <button
                onClick={() => void getChecks()}
                disabled={loading !== null || !hasCreds || !getEntityIdInput}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'checks' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span> : 'Get Checks'}
              </button>
            )}
            {selectedEndpoint === 'get-entity' && (
              <button
                onClick={() => void getEntity()}
                disabled={loading !== null || !hasCreds || !getEntityIdInput}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'get' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span> : 'Get Entity'}
              </button>
            )}
            {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
            {entityId && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
                <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
                <CopyButton text={entityId} />
                <button onClick={() => { setEntityId(null); setResult(null); setFullResult(null) }} className="text-[#5b6b85] hover:text-red-400 transition-colors"><X size={10} /></button>
              </div>
            )}
          </div>

          <div className="text-[9px] font-mono text-[#3f4f6b]">
            <span className="text-[#5b6b85]">Base:</span> {V1_BASE}
          </div>
        </div>
      </div>

      {/* ── COLUMN 3: Result ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor value={editorText} dirty={editorDirty} error={editorError} onChange={handleEditorChange} onReset={resetEditor} />
        )}
        <CurlCollapsible
          method={epDef.method}
          url={`${V1_BASE}${dynamicPath}`}
          creds={v1Creds}
          body={isGetEndpoint ? undefined : (editorDirty ? ((() => { try { return JSON.parse(editorText) as Record<string, unknown> } catch { return payload } })()) : payload)}
        />

        <InlineWebhookPanel entityId={entityId} customerId={creds.customerId || null} />

        {!isRunning && !result && (
          <div className="flex flex-col items-center justify-center py-16 rounded-[14px]" style={{ border: '2px dashed #233149', background: '#0d1626' }}>
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
              <Zap size={22} className="text-[#6366f1]" />
            </div>
            <p className="text-[14px] font-semibold text-[#5b6b85]">V1 KYC — synchronous result</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">Select a test persona or fill in the form, then click the action button</p>
          </div>
        )}

        {isRunning && (
          <div className="p-5 rounded-[14px] space-y-4" style={{ background: '#0e1828', border: '1px solid #2a3754' }}>
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="animate-spin text-amber-400 shrink-0" />
              <p className="text-[13px] font-semibold text-[#fcd34d]">Verifying…</p>
            </div>
          </div>
        )}

        {!isRunning && result && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            {scenarioIdx !== null && selectedEndpoint === 'create-verify' && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.2)' }}>
                <span className="text-[10px] text-[#8aa0c2]">Expected:</span>
                <span className="text-[10px] font-bold" style={{ color: KYC_V1_SCENARIOS[scenarioIdx].expectedResult === 'PASS' ? '#34d399' : KYC_V1_SCENARIOS[scenarioIdx].expectedResult === 'FAIL' ? '#f87171' : '#fb923c' }}>
                  {KYC_V1_SCENARIOS[scenarioIdx].expectedResult}
                </span>
                <span className="text-[10px] text-[#5b6b85] ml-auto">V1 API · synchronous</span>
              </div>
            )}
            {!result.entityProfileResult && selectedEndpoint === 'create-verify' && (
              <div className="p-3 rounded-[9px]" style={{ background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.2)' }}>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={12} className="text-amber-400" />
                  <span className="text-[11px] text-amber-400">No entityProfileResult in response — see raw JSON</span>
                </div>
              </div>
            )}
            <V1ResultPanel data={result} fullResponse={fullResult} />
          </div>
        )}
      </div>
    </div>
  )
}
